import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ShieldCheck, Activity, Gauge, Car } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const LandingPage = () => {
  const [vin, setVin] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    const cleanVin = vin.trim().toUpperCase();
    
    if (cleanVin.length !== 17) {
      toast.error('VIN must be exactly 17 characters');
      return;
    }
    
    navigate(`/search/${cleanVin}`);
  };

  const handleVinChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
    if (value.length <= 17) {
      setVin(value);
    }
  };

  const features = [
    {
      icon: <Activity className="h-8 w-8" />,
      title: 'Instant Report',
      description: 'Get complete vehicle information in seconds using official NHTSA database'
    },
    {
      icon: <ShieldCheck className="h-8 w-8" />,
      title: 'Verified Data',
      description: 'Access official government data for accurate vehicle specifications'
    },
    {
      icon: <Gauge className="h-8 w-8" />,
      title: 'Technical Specs',
      description: 'Detailed information including engine, transmission, and safety features'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1762314908735-6c7fab306f86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBzcG9ydHMlMjBjYXIlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzM3NTYyMzR8MA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background z-0" />
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 mb-8"
            >
              <Car className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">NHTSA Official Database</span>
            </motion.div>
            
            <h1 className="heading-font text-6xl sm:text-7xl lg:text-8xl font-black mb-6 tracking-tight">
              VIN CHECK
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
              Enter your VIN to get complete vehicle information instantly
            </p>
            
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 tracing-beam-input">
                  <Input
                    type="text"
                    placeholder="Enter VIN number"
                    value={vin}
                    onChange={handleVinChange}
                    autoComplete="off"
                    spellCheck="false"
                    className="h-14 w-full text-lg font-mono tracking-widest uppercase bg-input/50 border-transparent focus:border-primary placeholder:normal-case placeholder:tracking-normal placeholder:font-sans"
                    data-testid="vin-input"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="h-14 px-8 rounded-sm font-bold tracking-wide bg-primary hover:bg-primary/90 text-white"
                  data-testid="search-button"
                >
                  <Search className="h-5 w-5 mr-2" />
                  VIN SEARCH
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-4 text-center">
                Example: 1HGBH41JXMN109186
              </p>
            </form>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span>Official NHTSA Data</span>
              </div>
              <div className="w-1 h-1 bg-muted-foreground/30 rounded-full" />
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" />
                <span>Free & Fast</span>
              </div>
              <div className="w-1 h-1 bg-muted-foreground/30 rounded-full" />
              <div className="flex items-center gap-2">
                <Gauge className="h-4 w-4 text-primary" />
                <span>Detailed Specs</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      <section className="py-20 bg-gradient-to-b from-background to-card/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl sm:text-5xl font-bold mb-4">
              Why Choose VIN Check?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Powered by official NHTSA database for accurate vehicle information
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-card border border-border/50 p-8 rounded-sm hover:border-primary/50 transition-colors duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="inline-flex p-3 bg-primary/10 rounded-sm mb-4 text-primary">
                  {feature.icon}
                </div>
                <h3 className="heading-font text-xl font-bold mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default LandingPage;